import { workDaysOn } from '@/lib/utils/work-days/work-days';

/**
 * "Overtime Leave": the leave category with no allotment of its own, paid from the employee's overtime
 * balance (blocks/workflows/leave/lib/overtime-leave.js). It is known by its title, like "Sick Leave".
 */
export const OVERTIME_LEAVE_TITLE = 'Overtime Leave';

export const isOvertimeLeave = (title?: string) => (title ?? '').trim() === OVERTIME_LEAVE_TITLE;

const DEFAULT_DAY_HOURS = 8;
const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

export interface HrTermDayLength {
  AvgDailyWorkHour?: number | null;
  WorkDays?:
    { WorkDay: string; WorkingHours?: number | null; EffectiveFrom?: string | null }[] | null;
}

/** The length of the employee's work day on `day` ("YYYY-MM-DD") in hours: the HR term's
 * AvgDailyWorkHour, else the mean of its work days, else 8 h (the workflows' `dailyWorkSecondsOf`, legacy
 * ConvertSecondsIntoDays). A term whose work days change has one AvgDailyWorkHour (its latest setting), so
 * it takes the mean of the work days in force on `day` instead. */
export const dailyWorkHoursOf = (term: HrTermDayLength | null | undefined, day: string): number => {
  const avg = Number(term?.AvgDailyWorkHour ?? 0);
  const changes = (term?.WorkDays ?? []).some((row) => Boolean(row?.EffectiveFrom));
  if (avg > 0 && !changes) return avg;
  const days = workDaysOn(term?.WorkDays, day);
  const hours =
    days.length > 0
      ? days.map((day) => Number(day.WorkingHours ?? 0))
      : WEEKDAYS.map(() => DEFAULT_DAY_HOURS);
  const working = hours.filter((value) => value > 0);
  return working.length > 0
    ? working.reduce((sum, value) => sum + value, 0) / working.length
    : DEFAULT_DAY_HOURS;
};

/** Overtime seconds as days of the employee's work day on `day`, to two decimals. */
export const overtimeDaysOf = (
  seconds: number,
  term: HrTermDayLength | null | undefined,
  day: string
) => Math.round((seconds / 3600 / dailyWorkHoursOf(term, day)) * 100) / 100;
